
import { motion } from 'framer-motion';
import { useState } from 'react';

const GalleryGrid = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const categories = [
    {
      id: 1,
      title: 'Book Launch Events',
      description: 'Celebrating new releases with readers and fellow authors',
      images: [
        {
          id: 1,
          url: 'https://public.readdy.ai/ai/img_res/d7e2d5c6e755c5c8b7e60afb4a6ba6eb.jpg',
          caption: 'Echoes of Tomorrow Launch - New York, 2024',
        },
        {
          id: 2,
          url: 'https://readdy.ai/api/search-image?query=author%20speaking%20at%20literary%20event%20engaged%20audience%20warm%20ambient%20lighting%20professional%20book%20presentation%20sophisticated%20setting%20burgundy%20accents&width=800&height=600&seq=gallery-event-002&orientation=landscape',
          caption: 'Literary Festival Panel Discussion',
        },
        {
          id: 3,
          url: 'https://readdy.ai/api/search-image?query=intimate%20book%20reading%20event%20cozy%20bookstore%20setting%20author%20with%20readers%20warm%20lighting%20literary%20atmosphere%20cream%20and%20burgundy%20tones&width=800&height=600&seq=gallery-event-003&orientation=landscape',
          caption: 'Independent Bookstore Reading',
        },
      ],
    },
    {
      id: 2,
      title: 'Writing Process',
      description: 'A glimpse into the creative journey behind each story',
      images: [
        {
          id: 4,
          url: 'https://readdy.ai/api/search-image?query=author%20writing%20at%20elegant%20desk%20surrounded%20by%20books%20and%20notes%20warm%20natural%20light%20peaceful%20creative%20workspace%20vintage%20typewriter%20burgundy%20and%20cream%20aesthetic&width=600&height=800&seq=gallery-writing-001&orientation=portrait',
          caption: 'Morning Writing Session',
        },
        {
          id: 5,
          url: 'https://readdy.ai/api/search-image?query=close-up%20of%20handwritten%20manuscript%20pages%20with%20editing%20notes%20vintage%20fountain%20pen%20warm%20lighting%20literary%20workspace%20cream%20paper%20burgundy%20ink&width=600&height=800&seq=gallery-writing-002&orientation=portrait',
          caption: 'Manuscript Revisions',
        },
        {
          id: 6,
          url: 'https://readdy.ai/api/search-image?query=author%20research%20materials%20vintage%20books%20open%20notebooks%20warm%20study%20lighting%20sophisticated%20workspace%20burgundy%20leather%20bound%20books%20cream%20pages&width=600&height=800&seq=gallery-writing-003&orientation=portrait',
          caption: 'Research and Inspiration',
        },
      ],
    },
    {
      id: 3,
      title: 'Reader Connections',
      description: 'Meaningful moments with the literary community',
      images: [
        {
          id: 7,
          url: 'https://readdy.ai/api/search-image?query=author%20meeting%20with%20book%20club%20group%20warm%20friendly%20atmosphere%20engaged%20readers%20discussing%20books%20cozy%20setting%20burgundy%20and%20cream%20tones&width=600&height=800&seq=gallery-readers-001&orientation=portrait',
          caption: 'Book Club Visit',
        },
        {
          id: 8,
          url: 'https://readdy.ai/api/search-image?query=author%20signing%20personalized%20books%20for%20readers%20warm%20interaction%20literary%20event%20sophisticated%20setting%20burgundy%20accents%20professional%20photography&width=600&height=800&seq=gallery-readers-002&orientation=portrait',
          caption: 'Personalized Book Signing',
        },
        {
          id: 9,
          url: 'https://readdy.ai/api/search-image?query=author%20teaching%20creative%20writing%20workshop%20students%20engaged%20warm%20educational%20setting%20literary%20atmosphere%20burgundy%20and%20cream%20aesthetic&width=600&height=800&seq=gallery-readers-003&orientation=portrait',
          caption: 'Creative Writing Workshop',
        },
      ],
    },
    {
      id: 4,
      title: 'Literary Inspirations',
      description: 'Places and moments that fuel the creative spirit',
      images: [
        {
          id: 10,
          url: 'https://readdy.ai/api/search-image?query=peaceful%20library%20interior%20with%20vintage%20books%20warm%20natural%20light%20elegant%20architecture%20literary%20sanctuary%20burgundy%20and%20cream%20tones%20sophisticated%20aesthetic&width=800&height=600&seq=gallery-inspiration-001&orientation=landscape',
          caption: 'Historic Library Visit',
        },
        {
          id: 11,
          url: 'https://readdy.ai/api/search-image?query=serene%20coastal%20landscape%20peaceful%20ocean%20view%20inspiring%20natural%20scenery%20warm%20golden%20light%20literary%20inspiration%20burgundy%20sunset%20tones&width=800&height=600&seq=gallery-inspiration-002&orientation=landscape',
          caption: 'Coastal Writing Retreat',
        },
        {
          id: 12,
          url: 'https://readdy.ai/api/search-image?query=charming%20independent%20bookstore%20interior%20cozy%20reading%20nooks%20vintage%20books%20warm%20lighting%20literary%20haven%20burgundy%20and%20cream%20aesthetic&width=800&height=600&seq=gallery-inspiration-003&orientation=landscape',
          caption: 'Favorite Independent Bookstore',
        },
      ],
    },
  ];

  return (
    <section className="py-24 px-8 lg:px-16 bg-light-cream">
      <div className="max-w-7xl mx-auto">
        {categories.map((category, catIndex) => (
          <div key={category.id} className="mb-20 last:mb-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="font-playfair text-4xl font-bold text-charcoal mb-3">
                {category.title}
              </h2>
              <p className="font-lato text-warm-gray">{category.description}</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.images.map((image, imgIndex) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: imgIndex * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedImage(image.id)}
                >
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img
                        src={image.url}
                        alt={image.caption}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="p-4">
                      <p className="font-lato text-sm text-charcoal">{image.caption}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedImage !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-8"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-8 right-8 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
            onClick={() => setSelectedImage(null)}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <div className="max-w-5xl w-full">
            {categories.map((cat) =>
              cat.images.map(
                (img) =>
                  img.id === selectedImage && (
                    <div key={img.id}>
                      <img
                        src={img.url}
                        alt={img.caption}
                        className="w-full h-auto rounded-lg shadow-2xl"
                      />
                      <p className="font-lato text-white text-center mt-6 text-lg">
                        {img.caption}
                      </p>
                    </div>
                  )
              )
            )}
          </div>
        </motion.div>
      )}
    </section>
  );
};

export default GalleryGrid;
